import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';
import {
  CustomSection,
  CustomFieldDefinition,
  SystemFieldVisibility,
  ListMaster,
  ListMasterOption,
  FieldTypeOption,
  EntityOption,
  ValidationRulesSchema,
  PaginatedResponse,
  CustomFormCreateInput,
  CustomFieldCreateInput,
  ListMasterCreateInput,
  ListMasterOptionCreateInput,
  FormSchema,
} from '../../types/customForms.types';

export const customFormsApi = createApi({
  reducerPath: 'customFormsApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['CustomSection', 'CustomField', 'SystemField', 'ListMaster', 'ListMasterOption', 'FormSchema'],
  keepUnusedDataFor: 120,
  endpoints: (builder) => ({
    // Custom Sections
    getCustomSections: builder.query<
      PaginatedResponse<CustomSection>,
      { content_type?: string; page?: number; limit?: number }
    >({
      query: (params) => ({
        url: 'custom-forms/sections/',
        method: 'GET',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'CustomSection' as const, id })),
              { type: 'CustomSection', id: 'LIST' },
            ]
          : [{ type: 'CustomSection', id: 'LIST' }],
    }),

    getCustomSectionById: builder.query<CustomSection, string>({
      query: (id) => `custom-forms/sections/${id}/`,
      providesTags: (result, error, id) => [{ type: 'CustomSection', id }],
    }),

    createCustomSection: builder.mutation<CustomSection, CustomFormCreateInput>({
      query: (section) => ({
        url: 'custom-forms/sections/',
        method: 'POST',
        body: section,
      }),
      invalidatesTags: [{ type: 'CustomSection', id: 'LIST' }],
    }),

    updateCustomSection: builder.mutation<CustomSection, { id: string; section: Partial<CustomFormCreateInput> }>({
      query: ({ id, section }) => ({
        url: `custom-forms/sections/${id}/`,
        method: 'PATCH',
        body: section,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'CustomSection', id },
        { type: 'CustomSection', id: 'LIST' },
      ],
    }),

    deleteCustomSection: builder.mutation<void, string>({
      query: (id) => ({
        url: `custom-forms/sections/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'CustomSection', id: 'LIST' }],
    }),

    reorderCustomSections: builder.mutation<void, { order: string[] }>({
      query: (data) => ({
        url: 'custom-forms/sections/reorder/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'CustomSection', id: 'LIST' }],
    }),

    // Custom Fields
    getCustomFields: builder.query<
      PaginatedResponse<CustomFieldDefinition>,
      { sectionId: string; page?: number; limit?: number }
    >({
      query: ({ sectionId, ...params }) => ({
        url: `custom-forms/custom-fields/`,
        method: 'GET',
        params: {
          section: sectionId,
          ...params,
        },
      }),
      providesTags: (result, error, { sectionId }) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'CustomField' as const, id })),
              { type: 'CustomField', id: `SECTION_${sectionId}` },
            ]
          : [{ type: 'CustomField', id: `SECTION_${sectionId}` }],
    }),

    getCustomFieldById: builder.query<CustomFieldDefinition, { sectionId: string; fieldId: string }>({
      query: ({ sectionId, fieldId }) => `custom-forms/custom-fields/${sectionId}/`,
      providesTags: (result, error, { fieldId }) => [{ type: 'CustomField', id: fieldId }],
    }),

    getCustomFieldBySectionId: builder.query<CustomFieldDefinition[], { sectionId: string }>({
      query: ({ sectionId}) => `custom-forms/sections/${sectionId}/fields/`,
      providesTags: (result, error, { sectionId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'CustomField' as const, id })),
              { type: 'CustomField', id: `SECTION_${sectionId}` },
            ]
          : [{ type: 'CustomField', id: `SECTION_${sectionId}` }],
    }),

    createCustomField: builder.mutation<
      CustomFieldDefinition,
      { sectionId: string; field: CustomFieldCreateInput }
    >({
      query: ({ sectionId, field }) => ({
        url: `custom-forms/custom-fields/`,
        method: 'POST',
        body: field,
      }),
      invalidatesTags: (result, error, { sectionId }) => [
        { type: 'CustomField', id: `SECTION_${sectionId}` },
        { type: 'FormSchema', id: 'LIST' },
      ],
    }),

    updateCustomField: builder.mutation<
      CustomFieldDefinition,
      { sectionId: string; fieldId: string; field: Partial<CustomFieldCreateInput> }
    >({
      query: ({ sectionId, fieldId, field }) => ({
        url: `custom-forms/custom-fields/${fieldId}/`,
        method: 'PATCH',
        body: field,
      }),
      invalidatesTags: (result, error, { sectionId, fieldId }) => [
        { type: 'CustomField', id: fieldId },
        { type: 'CustomField', id: `SECTION_${sectionId}` },
        { type: 'FormSchema', id: 'LIST' },
      ],
    }),

    deleteCustomField: builder.mutation<void, { sectionId: string; fieldId: string }>({
      query: ({ sectionId, fieldId }) => ({
        url: `custom-forms/custom-fields/${fieldId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { sectionId }) => [
        { type: 'CustomField', id: `SECTION_${sectionId}` },
        { type: 'FormSchema', id: 'LIST' },
      ],
    }),

    reorderCustomFields: builder.mutation<void, { order: string[] }>({
      query: (data) => ({
        url: 'custom-forms/custom-fields/reorder/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'CustomField', id: 'LIST' }],
    }),

    // System Fields
    getSystemFields: builder.query<SystemFieldVisibility[], { content_type: string }>({
      query: (params) => ({
        url: 'custom-forms/system-fields/',
        method: 'GET',
        params,
      }),
      providesTags: (result, error, { content_type }) =>
        result
          ? [
              ...result.map(({ slug }) => ({ type: 'SystemField' as const, id: slug })),
              { type: 'SystemField', id: `CONTENT_TYPE_${content_type}` },
            ]
          : [{ type: 'SystemField', id: `CONTENT_TYPE_${content_type}` }],
    }),

    updateSystemField: builder.mutation<
      SystemFieldVisibility,
      { slug: string; content_type_id: string; field: Partial<SystemFieldVisibility> }
    >({
      query: ({ slug, content_type_id, field }) => ({
        url: `custom-forms/system-fields/${slug}/`,
        method: 'PATCH',
        body: field,
        params: { content_type: content_type_id },
      }),
      invalidatesTags: (result, error, { slug, content_type_id }) => [
        { type: 'SystemField', id: slug },
        { type: 'SystemField', id: `CONTENT_TYPE_${content_type_id}` },
        { type: 'FormSchema', id: 'LIST' },
      ],
    }),

    // List Masters
    getListMasters: builder.query<
      PaginatedResponse<ListMaster>,
      { search?: string; is_active?: boolean; page?: number; limit?: number }
    >({
      query: (params) => ({
        url: 'custom-forms/list-masters/',
        method: 'GET',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'ListMaster' as const, id })),
              { type: 'ListMaster', id: 'LIST' },
            ]
          : [{ type: 'ListMaster', id: 'LIST' }],
    }),

    getListMasterById: builder.query<ListMaster, string>({
      query: (id) => `custom-forms/list-masters/${id}/`,
      providesTags: (result, error, id) => [{ type: 'ListMaster', id }],
    }),

    createListMaster: builder.mutation<ListMaster, ListMasterCreateInput>({
      query: (listMaster) => ({
        url: 'custom-forms/list-masters/',
        method: 'POST',
        body: listMaster,
      }),
      invalidatesTags: [{ type: 'ListMaster', id: 'LIST' }],
    }),

    updateListMaster: builder.mutation<ListMaster, { id: string; listMaster: Partial<ListMasterCreateInput> }>({
      query: ({ id, listMaster }) => ({
        url: `custom-forms/list-masters/${id}/`,
        method: 'PATCH',
        body: listMaster,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'ListMaster', id },
        { type: 'ListMaster', id: 'LIST' },
      ],
    }),

    deleteListMaster: builder.mutation<void, string>({
      query: (id) => ({
        url: `custom-forms/list-masters/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'ListMaster', id: 'LIST' }],
    }),

    // List Master Options
    getListMasterOptions: builder.query<
      PaginatedResponse<ListMasterOption>,
      { listMasterId: string; page?: number; limit?: number }
    >({
      query: ({ listMasterId, ...params }) => ({
        url: `custom-forms/list-master-options/`,
        method: 'GET',
        params,
      }),
      providesTags: (result, error, { listMasterId }) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'ListMasterOption' as const, id })),
              { type: 'ListMasterOption', id: `LIST_MASTER_${listMasterId}` },
            ]
          : [{ type: 'ListMasterOption', id: `LIST_MASTER_${listMasterId}` }],
    }),

    getListMasterOptionsById: builder.query<
      ListMasterOption[],
      string
    >({
      query: (listMasterId) => ({
        url: `custom-forms/list-masters/${listMasterId}/options/`,
        method: 'GET',
      }),
      providesTags: (result, error, listMasterId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'ListMasterOption' as const, id })),
              { type: 'ListMasterOption', id: `LIST_MASTER_${listMasterId}` },
            ]
          : [{ type: 'ListMasterOption', id: `LIST_MASTER_${listMasterId}` }],
    }),

    getListMasterOptionsDropdown: builder.query<
      ListMasterOption[],
      string
    >({
      query: (listMasterId) => ({
        url: `custom-forms/list-masters/${listMasterId}/options/dropdown/`,
        method: 'GET',
      }),
      providesTags: (result, error, listMasterId) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'ListMasterOption' as const, id })),
              { type: 'ListMasterOption', id: `LIST_MASTER_${listMasterId}` },
            ]
          : [{ type: 'ListMasterOption', id: `LIST_MASTER_${listMasterId}` }],
    }),

    // Paginated + searchable variant of getListMasterOptionsDropdown, for
    // infinite-scroll dropdowns rendering large list masters.
    getListMasterOptionsDropdownPaginated: builder.query<
      PaginatedResponse<ListMasterOption>,
      { listMasterId: string; page?: number; limit?: number; search?: string }
    >({
      query: ({ listMasterId, ...params }) => ({
        url: `custom-forms/list-masters/${listMasterId}/options/dropdown/`,
        method: 'GET',
        params: { ...params, paginate: true },
      }),
      providesTags: (result, error, { listMasterId }) =>
        result
          ? [
              ...result.results.map(({ id }) => ({ type: 'ListMasterOption' as const, id })),
              { type: 'ListMasterOption', id: `LIST_MASTER_${listMasterId}` },
            ]
          : [{ type: 'ListMasterOption', id: `LIST_MASTER_${listMasterId}` }],
    }),

    createListMasterOption: builder.mutation<
      ListMasterOption,
      { listMasterId: string; option: ListMasterOptionCreateInput }
    >({
      query: ({ listMasterId, option }) => ({
        url: `custom-forms/list-master-options/`,
        method: 'POST',
        body: option,
      }),
      invalidatesTags: (result, error, { listMasterId }) => [
        { type: 'ListMasterOption', id: `LIST_MASTER_${listMasterId}` },
        { type: 'ListMaster', id: listMasterId },
      ],
    }),

    updateListMasterOption: builder.mutation<
      ListMasterOption,
      { listMasterId: string; optionId: string; option: Partial<ListMasterOptionCreateInput> }
    >({
      query: ({ listMasterId, optionId, option }) => ({
        url: `custom-forms/list-master-options/${optionId}/`,
        method: 'PATCH',
        body: option,
      }),
      invalidatesTags: (result, error, { listMasterId, optionId }) => [
        { type: 'ListMasterOption', id: optionId },
        { type: 'ListMasterOption', id: `LIST_MASTER_${listMasterId}` },
        { type: 'ListMaster', id: listMasterId },
      ],
    }),

    deleteListMasterOption: builder.mutation<void, { listMasterId: string; optionId: string }>({
      query: ({ listMasterId, optionId }) => ({
        url: `custom-forms/list-master-options/${optionId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { listMasterId }) => [
        { type: 'ListMasterOption', id: `LIST_MASTER_${listMasterId}` },
        { type: 'ListMaster', id: listMasterId },
      ],
    }),

    reorderListMasterOptions: builder.mutation<void, { listMasterId: string; order: string[] }>({
      query: ({ listMasterId, order }) => ({
        url: `custom-forms/list-master-options/reorder/`,
        method: 'POST',
        body: { order },
      }),
      invalidatesTags: (result, error, { listMasterId }) => [
        { type: 'ListMasterOption', id: `LIST_MASTER_${listMasterId}` },
        { type: 'ListMaster', id: listMasterId },
      ],
    }),

    // Utility endpoints
    getFieldTypes: builder.query<FieldTypeOption[], void>({
      query: () => 'custom-forms/field-types/',
    }),

    getFieldTypeValidationRules: builder.query<ValidationRulesSchema, string>({
      query: (fieldType) => `custom-forms/field-types/${fieldType}/validation-rules/`,
    }),

    getEntityOptions: builder.query<EntityOption[], void>({
      query: () => 'custom-forms/entity-options/',
    }),

    getEntityContentTypes: builder.query<EntityOption[], void>({
      query: () => 'custom-forms/entity-content-types/',
    }),

    getCustomFormMenu: builder.query<EntityOption[], void>({
      query: () => 'custom-forms/menu/',
    }),

    // Form Schema API - for dynamic form rendering
    getEntityFormSchema: builder.query<FormSchema, string>({
      query: (contentTypeId) => `custom-forms/entity-form-schema/${contentTypeId}/`,
      providesTags: (result, error, contentTypeId) => [
        { type: 'FormSchema', id: contentTypeId },
      ],
    }),
  }),
});

// Export hooks
export const {
  useGetCustomSectionsQuery,
  useGetCustomSectionByIdQuery,
  useCreateCustomSectionMutation,
  useUpdateCustomSectionMutation,
  useDeleteCustomSectionMutation,
  useReorderCustomSectionsMutation,
  useGetCustomFieldsQuery,
  useGetCustomFieldByIdQuery,
  useGetCustomFieldBySectionIdQuery,
  useCreateCustomFieldMutation,
  useUpdateCustomFieldMutation,
  useDeleteCustomFieldMutation,
  useReorderCustomFieldsMutation,
  useGetSystemFieldsQuery,
  useUpdateSystemFieldMutation,
  useGetListMastersQuery,
  useGetListMasterByIdQuery,
  useCreateListMasterMutation,
  useUpdateListMasterMutation,
  useDeleteListMasterMutation,
  useGetListMasterOptionsQuery,
  useGetListMasterOptionsByIdQuery,
  useGetListMasterOptionsDropdownQuery,
  useGetListMasterOptionsDropdownPaginatedQuery,
  useCreateListMasterOptionMutation,
  useUpdateListMasterOptionMutation,
  useDeleteListMasterOptionMutation,
  useReorderListMasterOptionsMutation,
  useGetFieldTypesQuery,
  useGetFieldTypeValidationRulesQuery,
  useGetEntityOptionsQuery,
  useGetEntityContentTypesQuery,
  useGetCustomFormMenuQuery,
  useGetEntityFormSchemaQuery,
} = customFormsApi;